

import { uuid4 } from './uuid'

export const STORAGE_KEY = 'renew:consent-queue:v1'

const MAX_ATTEMPTS = 12
const BASE_DELAY_MS = 1000
const MAX_DELAY_MS = 5 * 60 * 1000

// 409 is deliberately absent. It does not mean "already recorded" - it means this key was
// already spent on a *different* decision, and the journal wrote nothing. Dropping it here is
// how a consent disappears while the interface reports success. "Already recorded" is a 200
// with the same consent_id, which succeeds through the normal path.
const TERMINAL_STATUSES = [400, 401, 403, 404, 422]

const KEY_CONFLICT_STATUS = 409

let isFlushing = false
let flushTimer = null

function readQueue() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch (e) {
    return []
  }
}

function writeQueue(items) {
  try {
    if (!items.length) {
      localStorage.removeItem(STORAGE_KEY)
      return
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items))
  } catch (e) {
    return
  }
}

function backoffDelay(attempts) {
  return Math.min(BASE_DELAY_MS * Math.pow(2, attempts), MAX_DELAY_MS)
}

function statusOf(error) {
  return error && error.response && error.response.status
}

function isTerminal(error) {
  return TERMINAL_STATUSES.indexOf(statusOf(error)) !== -1
}

function isKeyConflict(error) {
  return statusOf(error) === KEY_CONFLICT_STATUS
}

export function enqueue(decision) {
  const items = readQueue()

  items.push({
    key: decision.key,
    body: decision.body,
    attempts: 0,
    nextAttemptAt: Date.now(),
    createdAt: Date.now(),
  })

  writeQueue(items)
}

export function queueLength() {
  return readQueue().length
}

export async function flush(sender, onSent) {
  if (isFlushing) return
  isFlushing = true

  try {
    let items = readQueue()

    for (const item of items.slice()) {
      if (item.nextAttemptAt > Date.now()) continue

      try {
        const response = await sender(item)

        items = readQueue().filter((row) => row.key !== item.key)
        writeQueue(items)

        if (onSent) onSent(response, item)
      } catch (error) {
        items = readQueue()
        const row = items.find((entry) => entry.key === item.key)
        if (!row) continue

        // Giving up on a decision means the journal will never hold it, while the visitor
        // was told it was saved - the same silent hole the 409 handling above exists to
        // prevent. Nothing here can recover it, so at minimum it has to be visible: a 422
        // means the body itself is unacceptable and every future decision will fail the
        // same way until the cause is fixed.
        if (isTerminal(error) || row.attempts + 1 >= MAX_ATTEMPTS) {
          console.warn(
            'consent: dropping a decision that will never reach the journal',
            {
              key: item.key,
              action: item.body && item.body.action,
              status: statusOf(error) || 'no response',
              attempts: row.attempts + 1,
            },
          )
          writeQueue(items.filter((entry) => entry.key !== item.key))
          continue
        }

        // The key was spent on another decision, so this body never reached the journal.
        // Resend it under a fresh key. In normal operation the key is generated once per
        // decision and never changes, so getting here at all means a bug worth seeing.
        if (isKeyConflict(error)) {
          console.warn('consent: idempotency key reused, resending under a new one', item.key)
          row.key = uuid4()
        }

        row.attempts += 1
        row.nextAttemptAt = Date.now() + backoffDelay(row.attempts)
        writeQueue(items)
      }
    }
  } finally {
    isFlushing = false
  }

  scheduleNextFlush(sender, onSent)
}

function scheduleNextFlush(sender, onSent) {
  if (flushTimer) {
    clearTimeout(flushTimer)
    flushTimer = null
  }

  const items = readQueue()
  if (!items.length) return

  const earliest = items.reduce((min, item) => Math.min(min, item.nextAttemptAt), Infinity)
  const delay = Math.max(earliest - Date.now(), BASE_DELAY_MS)

  flushTimer = setTimeout(() => flush(sender, onSent), delay)
}
